/* eslint-disable dot-notation */

import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { backButton as bb, init } from "@telegram-apps/sdk-react";

import { AppRoute } from "@/constants/routes";
import { usePlatformFeature } from "@/hooks/platform";
import { useCatalogScrollPosition } from "@/hooks/useCatalogScrollPosition";

export const backButtonExclude: string[] = [
  AppRoute.wallet,
  AppRoute.catalog,
  AppRoute.news,
  AppRoute.market,
  AppRoute.settings,
  AppRoute.welcome,
];

const BackButtonTMA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { prepareForBackNavigation } = useCatalogScrollPosition();

  init();
  bb.mount();

  const handleBackClick = useCallback(() => {
    prepareForBackNavigation();
    navigate(-1);
  }, [navigate, prepareForBackNavigation]);

  useEffect(() => {
    bb.hide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!backButtonExclude.includes(location.pathname)) {
      bb.onClick(handleBackClick);
      bb.show();
    } else {
      bb.hide();
    }

    return () => {
      bb.offClick(handleBackClick);
    };
  }, [handleBackClick, location.pathname]);

  return null;
};

const BackButtonCapacitor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const platform = usePlatformFeature();
  const { prepareForBackNavigation } = useCatalogScrollPosition();

  const handleBackClick = useCallback(() => {
    prepareForBackNavigation();
    navigate(-1);
  }, [navigate, prepareForBackNavigation]);

  useEffect(() => {
    if (platform.usePlatformData().platform === "android" || platform.usePlatformData().platform === "ios") {
      if (typeof window !== "undefined" && "Capacitor" in window) {
        const capacitor = (window as unknown as Record<string, unknown>)["Capacitor"];

        if (
          typeof capacitor === "object" &&
          capacitor !== null &&
          "Plugins" in capacitor &&
          typeof capacitor.Plugins === "object" &&
          capacitor.Plugins !== null &&
          "StatusBar" in capacitor.Plugins
        ) {
          const statusBar = capacitor.Plugins.StatusBar;
          if (typeof statusBar === "object" && statusBar !== null) {
            if ("setBackgroundColor" in statusBar && typeof statusBar.setBackgroundColor === "function") {
              statusBar.setBackgroundColor({ color: "#17212b" });
            }

            if ("setStyle" in statusBar && typeof statusBar.setStyle === "function") {
              statusBar.setStyle({ style: "LIGHT" });
            }

            if ("show" in statusBar && typeof statusBar.show === "function") {
              statusBar.show();
            }
          }
        }
      }
    }
  }, [platform]);

  if (backButtonExclude.includes(location.pathname)) {
    return null;
  }

  const safeAreaTop =
    platform.usePlatformData().platform === "android" || platform.usePlatformData().platform === "ios"
      ? {
        paddingTop: "calc(env(safe-area-inset-top)",
        height: "calc(env(safe-area-inset-top) + 3rem)",
        alignItems: "flex-end",
      }
      : {};

  if (platform.usePlatformData().platform === "android" || platform.usePlatformData().platform === "ios") {
    return (
      <div className="bg-background fixed left-0 top-0 z-50 flex w-full items-end" style={safeAreaTop}>
        <button
          aria-label="Back"
          className="flex h-12 items-center px-4"
          type="button"
          onClick={handleBackClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleBackClick();
            }
          }}
        >
          <svg
            className="stroke-text"
            fill="none"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M15 19L8 12L15 5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          </svg>
        </button>
      </div>
    );
  }
  return null;
};

const BackButton = () => {
  const platform = usePlatformFeature();

  if (platform.usePlatformData().platform === "tma") return <BackButtonTMA />;

  if (platform.usePlatformData().platform === "android" || platform.usePlatformData().platform === "ios") {
    return <BackButtonCapacitor />;
  }

  return null;
};

export default BackButton;
